import { Box, Paper, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { ProformaActionBar } from '@/components/InvoiceDetailsModal/ActionsCard';
import { ActivityCard } from '@/components/InvoiceDetailsModal/ActivityCard';
import { ChainStrip } from '@/components/InvoiceDetailsModal/ChainStrip';
import {
  DocumentHeader,
  STATE_COLOR,
} from '@/components/InvoiceDetailsModal/DocumentHeader';
import {
  DocumentLayout,
  DocumentLoading,
} from '@/components/InvoiceDetailsModal/DocumentLayout';
import { DocumentView } from '@/components/InvoiceDetailsModal/DocumentView';
import { OfferLine } from '@/components/InvoiceDetailsModal/OfferLine';
import { OpportunityButton } from '@/components/InvoiceDetailsModal/OpportunityButton';
import { PartiesCard } from '@/components/InvoiceDetailsModal/PartiesCard';
import { ProformaPaymentCard } from '@/components/InvoiceDetailsModal/PaymentCard';
import { useInvoiceActions } from '@/components/InvoicesPage/useInvoiceActions';
import { TestBadge } from '@/components/TestBadge';
import { proformaStateLabel } from '@/lib/proformaState';
import {
  invalidateDocumentQueries,
  useDocumentGroup,
  useInvoiceDetails,
} from '@/models/invoices';

/** Re-reads the group, document area and Activity whenever the document's details change. */
export function useFollowDocument(
  updatedAt: number,
  id: number,
  placeholder: boolean,
) {
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (!updatedAt || placeholder) return;
    if (seen.current !== id) {
      seen.current = id;
      return;
    }
    invalidateDocumentQueries();
  }, [updatedAt, id, placeholder]);
}

/**
 * The proforma page (spec 9): on the left the related documents, Actions, Versions, Payment,
 * Parties and Activity; on the right the selected version as stored; the header with the state above both.
 */
export function ProformaDocument({ id }: { id: number }) {
  const details = useInvoiceDetails(id);
  const group = useDocumentGroup('proforma', id);
  const actions = useInvoiceActions();
  const [selected, setSelected] = useState<{ id: number; v: number | null }>({
    id,
    v: null,
  });
  const version = selected.id === id ? selected.v : null;
  const setVersion = (v: number | null) => setSelected({ id, v });
  useFollowDocument(details.dataUpdatedAt, id, details.isPlaceholderData);

  const invoice = details.data;
  if (details.isLoading) {
    return <DocumentLoading />;
  }
  if (!invoice) {
    return (
      <Text c="dimmed" p="md">
        Proforma not found, or you may not open it.
      </Text>
    );
  }

  const notSent =
    invoice.status === 'issued' &&
    (invoice.delivery?.state === 'never_sent' ||
      invoice.delivery?.state === 'outdated_at_client');

  return (
    <Box>
      <DocumentLayout
        header={
          <DocumentHeader
            number={invoice.invoiceNumber}
            action={<OpportunityButton group={group.data} />}
            stateLabel={proformaStateLabel(invoice.status)}
            stateColor={
              notSent ? 'orange' : (STATE_COLOR[invoice.status] ?? 'gray')
            }
            stateNote={notSent ? 'Not yet sent' : null}
            indicators={invoice.isTest && <TestBadge />}
          />
        }
        aside={
          <>
            {group.data && <ChainStrip group={group.data} />}
            <ProformaPaymentCard invoice={invoice} />
            <PartiesCard parties={invoice.parties} />
            <ActivityCard kind="proforma" id={invoice.id} />
          </>
        }
      >
        <Stack gap={8}>
          <OfferLine group={group.data} proforma={invoice} />
          <ProformaActionBar
            invoice={invoice}
            handlers={actions.handlers}
            group={group.data}
          />
        </Stack>
        <Paper withBorder p="md">
          <DocumentView
            kind="proforma"
            id={invoice.id}
            version={version}
            versions={invoice.documentVersions?.versions}
            onSelectVersion={setVersion}
          />
        </Paper>
      </DocumentLayout>
      {actions.modals}
    </Box>
  );
}
